import React, { useEffect, useState } from 'react';
import { auth } from './lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const FEATURES = [
  {
    icon: '⬛',
    title: 'Drop & Forget',
    body: 'Point SlateVault at any folder on your local drive. It watches 24/7 — the moment raw footage lands, the pipeline fires automatically.'
  },
  {
    icon: '⚡',
    title: 'Instant 720p Proxies',
    body: 'FFmpeg converts every clip to a streamable proxy in minutes. No manual transcoding. No waiting for editors to finish a drive transfer.'
  },
  {
    icon: '🧠',
    title: 'AI-Powered Logging',
    body: 'Gemini AI analyzes each clip and writes a description, tags, mood, shot type, and subject list. A job that took hours now takes seconds.'
  },
  {
    icon: '🔍',
    title: 'Search Everything',
    body: 'Find any clip by tag, mood, subject, or description. Type "golden hour interview close-up" and get exactly that — instantly.'
  },
  {
    icon: '☁️',
    title: 'Cloud-Accessible',
    body: 'Your entire footage library lives in a secure, searchable web dashboard. Share access with producers, directors, or clients from anywhere.'
  },
  {
    icon: '🔒',
    title: 'Your Files Stay Local',
    body: 'Original high-res files never leave your drive. Only the lightweight proxies are uploaded — you control the masters.'
  },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Install the Watcher',
    body: 'A lightweight Node.js script runs on your local machine and monitors your footage drives in real time.',
  },
  {
    step: '02',
    title: 'Drop Footage — Walk Away',
    body: 'Copy files to your production folder like you always do. SlateVault detects them, transcodes proxies, and uploads them automatically.',
  },
  {
    step: '03',
    title: 'Search & Screen',
    body: 'Open your portal from any browser. Every clip is tagged, described, and searchable. Click to preview. Share with your team.',
  },
];

export default function SlateVaultLanding() {
  const [user, setUser] = useState(undefined); // undefined = loading

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return unsub;
  }, []);

  const ctaHref = user ? '/#/slatevault/app' : '/#/slatevault/auth';
  const ctaLabel = user ? 'OPEN YOUR VAULT →' : 'GET STARTED — $49/MONTH →';

  return (
    <div className="min-h-screen bg-[#050505] text-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <style>{`
        .sv-btn-primary { background: #fff; color: #000; padding: 16px 32px; font-weight: 900; font-size: 0.8rem; letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none; display: inline-block; transition: all 0.2s; border: none; cursor: pointer; }
        .sv-btn-primary:hover { background: #00aaff; color: #fff; }
        .sv-btn-outline { background: transparent; color: #fff; padding: 16px 32px; font-weight: 900; font-size: 0.8rem; letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none; display: inline-block; border: 1px solid #333; transition: all 0.2s; cursor: pointer; }
        .sv-btn-outline:hover { border-color: #00aaff; color: #00aaff; }
        .feature-card:hover { border-color: #333 !important; }
      `}</style>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: 'rgba(5,5,5,0.97)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #111', padding: '14px 6%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <a href="/#/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/logo.png" alt="Aperture Slate" style={{ height: '36px' }} />
          <div>
            <div style={{ fontSize: '0.65rem', letterSpacing: '0.2em', color: '#00aaff', fontWeight: 700 }}>APERTURE SLATE</div>
            <div style={{ fontSize: '0.75rem', letterSpacing: '0.25em', color: '#fff', fontWeight: 900 }}>SLATEVAULT</div>
          </div>
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <a href="/#/tools" style={{ fontSize: '0.7rem', color: '#00aaff', letterSpacing: '0.15em', textDecoration: 'none', textTransform: 'uppercase' }}>All Tools</a>
          {user
            ? <a href="/#/slatevault/app" className="sv-btn-primary" style={{ padding: '8px 20px' }}>Open Vault</a>
            : <a href="/#/slatevault/auth" className="sv-btn-outline" style={{ padding: '8px 20px' }}>Sign In</a>
          }
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: '120px 6% 100px', maxWidth: '1200px', margin: '0 auto' }}>
        <p style={{ fontSize: '0.7rem', letterSpacing: '0.4em', color: '#00aaff', fontWeight: 700, textTransform: 'uppercase', marginBottom: '20px' }}>
          Aperture Slate // Media Asset Management
        </p>
        <h1 style={{ fontSize: 'clamp(3rem, 8vw, 6rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 0.9, margin: '0 0 30px 0', textTransform: 'uppercase' }}>
          Your Footage.<br />
          <span style={{ color: '#333' }}>Indexed.</span><br />
          Instantly.
        </h1>
        <p style={{ fontSize: '1.2rem', color: '#00aaff', lineHeight: 1.6, maxWidth: '580px', marginBottom: '48px' }}>
          SlateVault watches your local drives, auto-generates proxy files, and uses AI to tag every clip — so your entire library is searchable from any browser in minutes.
        </p>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <a href={ctaHref} className="sv-btn-primary">{ctaLabel}</a>
          <a href="#how-it-works" className="sv-btn-outline">SEE HOW IT WORKS</a>
        </div>
        <p style={{ fontSize: '0.75rem', color: '#00aaff', marginTop: '20px', letterSpacing: '0.05em' }}>
          $49/month · Billed monthly · Cancel anytime
        </p>
      </section>

      {/* Social proof bar */}
      <div style={{ borderTop: '1px solid #111', borderBottom: '1px solid #111', padding: '28px 6%', display: 'flex', gap: '40px', flexWrap: 'wrap', alignItems: 'center' }}>
        {['BRAW', 'MOV', 'MP4', 'MXF', 'R3D', 'DNG', 'MKV'].map(f => (
          <span key={f} style={{ fontSize: '0.7rem', letterSpacing: '0.2em', color: '#333', fontWeight: 700 }}>{f}</span>
        ))}
        <span style={{ fontSize: '0.7rem', letterSpacing: '0.15em', color: '#00aaff', marginLeft: 'auto' }}>All major camera formats supported</span>
      </div>

      {/* How it works */}
      <section id="how-it-works" style={{ padding: '100px 6%', maxWidth: '1200px', margin: '0 auto' }}>
        <p style={{ fontSize: '0.7rem', letterSpacing: '0.4em', color: '#00aaff', fontWeight: 700, textTransform: 'uppercase', marginBottom: '16px' }}>The Pipeline</p>
        <h2 style={{ fontSize: '3rem', fontWeight: 900, letterSpacing: '-0.03em', textTransform: 'uppercase', marginBottom: '60px' }}>How It Works</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0' }}>
          {HOW_IT_WORKS.map((step, i) => (
            <div key={i} style={{ padding: '40px', borderLeft: i === 0 ? 'none' : '1px solid #111', borderTop: '1px solid #111' }}>
              <div style={{ fontSize: '3rem', fontWeight: 900, color: '#1a1a1a', letterSpacing: '-0.05em', marginBottom: '20px' }}>{step.step}</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 900, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '12px' }}>{step.title}</h3>
              <p style={{ color: '#00aaff', fontSize: '0.9rem', lineHeight: 1.7 }}>{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section style={{ backgroundColor: '#080808', borderTop: '1px solid #111', padding: '100px 6%' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <p style={{ fontSize: '0.7rem', letterSpacing: '0.4em', color: '#00aaff', fontWeight: 700, textTransform: 'uppercase', marginBottom: '16px' }}>Everything Included</p>
          <h2 style={{ fontSize: '3rem', fontWeight: 900, letterSpacing: '-0.03em', textTransform: 'uppercase', marginBottom: '60px' }}>Built for Production</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1px', backgroundColor: '#111' }}>
            {FEATURES.map((f, i) => (
              <div key={i} className="feature-card" style={{ backgroundColor: '#080808', padding: '36px', border: '1px solid transparent', transition: 'border-color 0.2s' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '16px' }}>{f.icon}</div>
                <h3 style={{ fontSize: '1rem', fontWeight: 900, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '10px' }}>{f.title}</h3>
                <p style={{ color: '#00aaff', fontSize: '0.875rem', lineHeight: 1.7 }}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: '100px 6%', maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
        <p style={{ fontSize: '0.7rem', letterSpacing: '0.4em', color: '#00aaff', fontWeight: 700, textTransform: 'uppercase', marginBottom: '16px' }}>Simple Pricing</p>
        <h2 style={{ fontSize: '3rem', fontWeight: 900, letterSpacing: '-0.03em', textTransform: 'uppercase', marginBottom: '60px' }}>One Plan. Everything.</h2>

        <div style={{ border: '1px solid #222', backgroundColor: '#080808', padding: '60px', textAlign: 'left', maxWidth: '480px', margin: '0 auto 40px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '40px' }}>
            <div>
              <p style={{ fontSize: '0.7rem', letterSpacing: '0.3em', color: '#00aaff', textTransform: 'uppercase', marginBottom: '8px' }}>Studio Plan</p>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ fontSize: '3.5rem', fontWeight: 900, letterSpacing: '-0.04em' }}>$49</span>
                <span style={{ color: '#00aaff', fontSize: '0.9rem' }}>/month</span>
              </div>
            </div>
            <span style={{ fontSize: '0.65rem', backgroundColor: '#00aaff', color: '#000', padding: '4px 10px', fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase' }}>CURRENT</span>
          </div>

          {[
            'Unlimited footage folders watched',
            'Auto 720p proxy generation',
            'AI tagging on every clip (tags, mood, shot type, subjects)',
            'Full-text search across your library',
            'Secure cloud storage for proxies',
            'Originals stay on your local drives',
            'Access from any browser, anywhere',
            'Cancel anytime',
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
              <span style={{ color: '#00aaff', fontWeight: 900, flexShrink: 0, marginTop: '1px' }}>—</span>
              <span style={{ color: '#00aaff', fontSize: '0.875rem', lineHeight: 1.5 }}>{item}</span>
            </div>
          ))}

          <a href={ctaHref} className="sv-btn-primary" style={{ display: 'block', textAlign: 'center', marginTop: '40px' }}>
            {user ? 'OPEN YOUR VAULT →' : 'SUBSCRIBE — $49/MONTH →'}
          </a>
        </div>

        <p style={{ color: '#00aaff', fontSize: '0.75rem', lineHeight: 1.7 }}>
          Secure payments via Stripe. Cancel anytime from your account dashboard.<br />
          Infrastructure costs covered — Gemini AI, Firebase Storage, and Cloud Functions included.
        </p>
      </section>

      {/* Final CTA */}
      <section style={{ borderTop: '1px solid #111', padding: '100px 6%', textAlign: 'center', backgroundColor: '#050505' }}>
        <h2 style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', fontWeight: 900, letterSpacing: '-0.03em', textTransform: 'uppercase', marginBottom: '24px' }}>
          Stop Hunting Through<br /><span style={{ color: '#333' }}>Hard Drives.</span>
        </h2>
        <p style={{ color: '#00aaff', fontSize: '1rem', lineHeight: 1.7, maxWidth: '480px', margin: '0 auto 40px' }}>
          Your next project deserves a professional logging workflow. SlateVault is ready in under 10 minutes.
        </p>
        <a href={ctaHref} className="sv-btn-primary">{ctaLabel}</a>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid #111', padding: '40px 6%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <p style={{ fontSize: '0.7rem', color: '#00aaff', letterSpacing: '0.2em', margin: 0 }}>© 2026 APERTURE SLATE // SLATEVAULT</p>
        <div style={{ display: 'flex', gap: '24px' }}>
          <a href="/#/" style={{ fontSize: '0.7rem', color: '#00aaff', letterSpacing: '0.15em', textDecoration: 'none', textTransform: 'uppercase' }}>Main Site</a>
          <a href="/#/tools" style={{ fontSize: '0.7rem', color: '#00aaff', letterSpacing: '0.15em', textDecoration: 'none', textTransform: 'uppercase' }}>All Tools</a>
          <a href="/#/contact" style={{ fontSize: '0.7rem', color: '#00aaff', letterSpacing: '0.15em', textDecoration: 'none', textTransform: 'uppercase' }}>Contact</a>
        </div>
      </footer>
    </div>
  );
}
